import { useState } from "react"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import { Textarea } from "../components/ui/textarea"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog"
import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent
} from "../components/ui/tooltip"
import { Check, Pencil, RefreshCw, Video, Image as ImageIcon, Instagram, Play, Loader2 } from "lucide-react"

export function AdConcepts({
    concepts,
    campaigns,
    onGenerateCampaignConcepts,
    generatingCampaignIds,
    onToggleConcept,
    onSelect,
    onSelectImageConcept,
    onEdit,
    onRegenerate,
    regeneratingIds,
    generatingScriptId,
    onBack,
    currentProjectName
}) {
    const [conceptFilter, setConceptFilter] = useState("all")
    const [editingConcept, setEditingConcept] = useState(null)
    const [editValues, setEditValues] = useState({})
    const [isSavingEdit, setIsSavingEdit] = useState(false)

    concepts.find((c) => c.selected);

    const commonEditableFields = [
        { key: "hook", label: "Hook" },
        { key: "conceptDescription", label: "Concept Description" },
        { key: "whyThisWorks", label: "Why This Works" },
        { key: "expectedOutcome", label: "Expected Outcome" },
        { key: "benefit", label: "Benefit" },
        { key: "cta", label: "CTA" },
    ]

    const getEditableFields = (concept) => {
        const adType = concept?.adType || concept?.type
        const typeSpecificFields = adType === "static_image" || adType === "image"
            ? [
                { key: "creativeDirection", label: "Creative Direction" },
                { key: "headline", label: "Headline" },
            ]
            : [{ key: "script", label: "Script" }]

        return [commonEditableFields[0], ...typeSpecificFields, ...commonEditableFields.slice(1)]
    }

    const getEditableValue = (concept, key) => {
        if (key === "creativeDirection") {
            const creativeDirection = concept.creativeDirection ?? concept.script
            return typeof creativeDirection === "string" ? creativeDirection : ""
        }
        return typeof concept[key] === "string" ? concept[key] : ""
    }

    const openEditDialog = (concept) => {
        const initialValues = {}
        getEditableFields(concept).forEach(({ key }) => {
            initialValues[key] = getEditableValue(concept, key)
        })
        setEditingConcept(concept)
        setEditValues(initialValues)
    }

    const closeEditDialog = () => {
        if (isSavingEdit) return
        setEditingConcept(null)
        setEditValues({})
    }

    const getChangedFields = () => {
        if (!editingConcept) return {}

        return getEditableFields(editingConcept).reduce((changes, { key }) => {
            const originalValue = getEditableValue(editingConcept, key)
            if (editValues[key] !== originalValue) {
                changes[key] = editValues[key]
            }
            return changes
        }, {})
    }

    const changedFields = getChangedFields()
    const hasEditChanges = Object.keys(changedFields).length > 0

    const handleSaveEdit = async () => {
        if (!editingConcept || !hasEditChanges || isSavingEdit) return

        setIsSavingEdit(true)
        try {
            const updatedConcept = await onEdit(editingConcept.id || editingConcept._id, changedFields)
            if (updatedConcept) {
                setEditingConcept(null)
                setEditValues({})
            }
        } catch (error) {
            console.error("Failed to edit ad concept:", error)
        } finally {
            setIsSavingEdit(false)
        }
    }

    const filteredConcepts = concepts.filter((concept) => {
        if (conceptFilter === "all") return true
        if (conceptFilter === "ads") return concept.type === "ugc" || concept.type === "image" || concept.type === "video" || concept.type === "demo"
        if (conceptFilter === "organic") return concept.type === "organic-video" || concept.type === "organic-post"
        return true
    })

    const getConceptBadge = (type) => {
        switch (type) {
            case "ugc_video":
                return { label: "UGC Video Ad", icon: Video, color: "purple" }
            case "static_image":
                return { label: "Image Ad", icon: ImageIcon, color: "blue" }
            case "ugc":
                return { label: "UGC Video Ad", icon: Video, color: "purple" }
            case "image":
                return { label: "Image Ad", icon: ImageIcon, color: "blue" }
            case "organic-video":
                return { label: "Organic Video", icon: Instagram, color: "teal" }
            case "organic-post":
                return { label: "Organic Post", icon: Instagram, color: "teal" }
            case "demo":
                return { label: "Product Demo", icon: Play, color: "amber" }
            default:
                return { label: "Video Ad", icon: Video, color: "green" }
        }
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case "generated":
                return { label: "Generated", color: "green" }
            case "In_progress":
                return { label: "In Progress", color: "amber" }
            default:
                return { label: "AI Concept", color: "blue" }
        }
    }

    const getColorClasses = (color) => {
        const maps = {
            purple: "border-purple-500/30 text-purple-500 bg-purple-500/10 dark:border-purple-500/20 dark:text-purple-400 dark:bg-purple-500/10",
            blue: "border-blue-500/30 text-blue-500 bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-400 dark:bg-blue-500/10",
            teal: "border-teal-500/30 text-teal-600 bg-teal-500/10 dark:border-teal-500/20 dark:text-teal-400 dark:bg-teal-500/10",
            amber: "border-amber-500/30 text-amber-600 bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400 dark:bg-amber-500/10",
            green: "border-green-500/30 text-green-600 bg-green-500/10 dark:border-green-500/20 dark:text-green-400 dark:bg-green-500/10",
            gray: "border-gray-500/30 text-gray-600 bg-gray-500/10 dark:border-gray-500/20 dark:text-gray-400 dark:bg-gray-500/10",
        }
        return maps[color] || maps.gray
    }

    return (
        <div className="flex-1 min-w-0 overflow-y-auto py-6 px-4 sm:px-6 lg:px-10 bg-gray-50 dark:bg-[#0a0a0a]">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                        {currentProjectName && (
                            <p className="text-xs font-medium uppercase tracking-wider text-orange-500 dark:text-orange-400">
                                {currentProjectName}
                            </p>
                        )}
                        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                            Creative Concepts
                        </h1>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Select the concept you want to create
                        </p>
                    </div>

                    <div className="flex items-center self-start rounded-full p-1 bg-gray-100 dark:bg-white/10">
                        {[
                            { key: "all", label: "All" },
                            { key: "ads", label: "Ads" },
                            { key: "organic", label: "Organic" },
                        ].map((filter) => (
                            <button
                                key={filter.key}
                                onClick={() => setConceptFilter(filter.key)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${conceptFilter === filter.key
                                    ? "bg-orange-500 text-white"
                                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                    }`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-10">
                    {campaigns?.map((campaign) => {
                        const campaignConcepts = filteredConcepts.filter(c => c.campaignName === campaign.campaignName)
                        const isGenerating = generatingCampaignIds?.has(campaign._id)

                        return (
                            <div key={campaign._id || campaign.campaignName} className="space-y-4">
                                <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-gray-200 dark:border-white/10">
                                    <div className="min-w-0">
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {campaign.campaignName}
                                        </h2>
                                        {campaign.angle?.angleName && (
                                            <p className="text-xs font-medium uppercase tracking-wider text-orange-600 dark:text-orange-400 mt-1">
                                                Angle: {campaign.angle.angleName}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-sm font-medium text-gray-500">
                                        {campaign.adsToCreate} ads planned
                                    </div>
                                </div>

                                {campaign.conceptsGenerated ? (
                                    campaignConcepts.length > 0 ? (
                                        <div className="grid gap-4">
                                            {campaignConcepts.map((concept) => {
                                                const badge = getConceptBadge(concept.adType || concept.type)
                        const Icon = badge.icon
                        const statusBadge = getStatusBadge(concept.status)
                        const isSelected = concept.selected
                        const isRegenerating = regeneratingIds?.has(concept.id)
                        const isImageAd = (concept.adType || concept.type) === "static_image" || (concept.adType || concept.type) === "image"
                        const creativeDirection = concept.creativeDirection ?? concept.script ?? ""


                        return (
                            <div
                                key={concept.id}
                                onClick={() => onToggleConcept(concept.id)}
                                className={`group relative p-5 rounded-xl border transition-all cursor-pointer ${isSelected
                                    ? "bg-orange-50 border-orange-400 dark:bg-orange-500/10 dark:border-orange-500"
                                    : "bg-white border-gray-200 hover:border-gray-300 dark:bg-white/5 dark:border-white/10 dark:hover:border-white/20"
                                    }`}
                            >
                                <div className={`absolute top-4 right-4 flex items-center gap-1 transition-opacity ${isRegenerating ? "opacity-100" : "opacity-100"}`}>
                                    <TooltipProvider delayDuration={0}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        openEditDialog(concept)
                                                    }}
                                                    className="p-2 rounded-lg transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-white dark:hover:bg-white/10"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">Edit</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    disabled={isRegenerating}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRegenerate(concept.id);
                                                    }}
                                                    className={`p-2 rounded-lg transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-white dark:hover:bg-white/10 ${isRegenerating ? "cursor-not-allowed opacity-50" : ""}`}
                                                >
                                                    <RefreshCw className={`w-4 h-4 ${isRegenerating ? "animate-spin" : ""}`} />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">Regenerate Concept</TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div
                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${isSelected
                                            ? "bg-orange-500 border-orange-500"
                                            : "border-gray-300 dark:border-white/20"
                                            }`}
                                    >
                                        {isSelected && <Check className="w-4 h-4 text-white" />}
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge
                                                variant="outline"
                                                className={`text-[10px] font-medium ${getColorClasses(badge.color)}`}
                                            >
                                                <Icon className="w-3 h-3 mr-1" />
                                                {badge.label}
                                            </Badge>
                                            <Badge
                                                variant="outline"
                                                className={`text-[10px] font-medium ${getColorClasses(statusBadge.color)}`}
                                            >
                                                {statusBadge.label}
                                            </Badge>
                                        </div>

                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider mb-0.5 opacity-60 text-gray-500 dark:text-gray-400">
                                                Hook
                                            </p>
                                            <p className="text-base font-semibold leading-snug text-gray-900 dark:text-white">
                                                "{concept.hook}"
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider mb-0.5 opacity-60 text-gray-500 dark:text-gray-400">
                                                {isImageAd ? "Creative Direction" : "Script"}
                                            </p>
                                            <p className="text-sm leading-snug text-gray-700 dark:text-gray-300">
                                                {isImageAd ? (creativeDirection || "N/A") : (concept.script || "N/A")}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider mb-0.5 opacity-60 text-gray-500 dark:text-gray-400">
                                                CTA
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {concept.cta}
                                            </p>
                                        </div>

                                        <div className="p-2 rounded-lg bg-gray-50 dark:bg-white/5">
                                            <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5 opacity-60 text-gray-500 dark:text-gray-400">
                                                Why this works
                                            </p>
                                            <p className="text-xs leading-snug text-gray-500 dark:text-gray-400">
                                                {concept?.whyThisWorks}
                                            </p>
                                        </div>

                                        <div className="p-2 rounded-lg bg-green-50 dark:bg-green-500/10">
                                            <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5 opacity-60 text-green-600 dark:text-green-400">
                                                Expected outcome
                                            </p>
                                            <p className="text-xs leading-snug text-green-700 dark:text-green-300">
                                                {concept.expectedOutcome}
                                            </p>
                                        </div>

                                        <div
                                            className={`overflow-hidden transition-all duration-300 ease-out ${isSelected ? "max-h-32 opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"
                                                }`}
                                        >
                                            {isImageAd ? (
                                                <>
                                                    <p className="text-xs text-center mb-2 text-gray-500 dark:text-gray-400">
                                                        This will generate an image ad — no script needed
                                                    </p>
                                                    <button
                                                        disabled={generatingScriptId !== null}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onSelectImageConcept(concept.id)
                                                        }}
                                                        className="w-full h-12 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-xl text-base font-medium flex items-center justify-center transition-colors"

                                                    >
                                                        {generatingScriptId === concept.id ? (
                                                            <>
                                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                                Generating image ad...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ImageIcon className="w-5 h-5" />
                                                                Generate Image Ad
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-xs text-center mb-2 text-gray-500 dark:text-gray-400">
                                                        This is ready — review and tweak on the next step
                                                    </p>
                                                    <button
                                                        disabled={generatingScriptId !== null}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onSelect(concept.id)
                                                        }}
                                                        className="w-full h-12 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-xl text-base font-medium flex items-center justify-center transition-colors"
                                                    >
                                                        {generatingScriptId === concept.id ? (
                                                            <>
                                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                                Generating script...
                                                            </>
                                                        ) : (
                                                            "Use this concept"
                                                        )}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                                        </div>
                                    ) : (
                                        <div className="p-8 text-center text-gray-500 border border-dashed rounded-xl border-gray-200 dark:border-white/10 dark:text-gray-400">
                                            No concepts match the current filter.
                                        </div>
                                    )
                                ) : (
                                    <div className="p-8 flex flex-col items-center justify-center text-center border border-dashed rounded-xl bg-gray-50 border-gray-200 dark:bg-white/5 dark:border-white/10">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                            Concepts for this campaign haven't been generated yet.
                                        </p>
                                        <Button
                                            disabled={isGenerating}
                                            onClick={() => onGenerateCampaignConcepts(campaign._id)}
                                            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-10 px-6"
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                                    Generating concepts...
                                                </>
                                            ) : (
                                                "Generate concepts for this campaign"
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                <div className="flex justify-start items-center pt-4 pb-10">
                    <Button
                        variant="outline"
                        onClick={onBack}
                        className="h-10 px-6 rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                    >
                        Back to Plan
                    </Button>
                </div>
            </div>

            <Dialog
                open={Boolean(editingConcept)}
                onOpenChange={(open) => !open && closeEditDialog()}
                className="max-w-3xl"
            >
                <DialogContent
                    onClose={closeEditDialog}
                    className="max-h-[85vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle>Edit ad concept</DialogTitle>
                        <DialogDescription>
                            Update the creative copy. Campaign and ad type cannot be changed.
                        </DialogDescription>
                    </DialogHeader>

                    {editingConcept && (
                        <div className="mt-5 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                        Campaign
                                    </p>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                                        {editingConcept.campaignName || "—"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                        Ad type
                                    </p>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                                        {getConceptBadge(editingConcept.adType || editingConcept.type).label}
                                    </p>
                                </div>
                            </div>

                            {getEditableFields(editingConcept).map(({ key, label }) => (
                                <div key={key}>
                                    <label
                                        htmlFor={`concept-${key}`}
                                        className="block text-xs font-medium uppercase tracking-wider mb-2 text-gray-500 dark:text-gray-400"
                                    >
                                        {label}
                                    </label>
                                    <Textarea
                                        id={`concept-${key}`}
                                        value={editValues[key] ?? ""}
                                        onChange={(event) => setEditValues(prev => ({
                                            ...prev,
                                            [key]: event.target.value,
                                        }))}
                                        rows={4}
                                        disabled={isSavingEdit}
                                        className="w-full rounded-lg resize-y bg-white border-gray-200 text-gray-900 dark:bg-white/5 dark:border-white/10 dark:text-white"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={closeEditDialog}
                            disabled={isSavingEdit}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSaveEdit}
                            disabled={!hasEditChanges || isSavingEdit}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                        >
                            {isSavingEdit ? (
                                <>
                                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
